import { withReturnTo } from './emailLinks';

export type NotificationTargetInput = {
  entityType?: string | null;
  entityId?: number | null;
  link?: string | null;
  data?: Record<string, unknown> | string | null;
};

function positiveId(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

function notificationData(value: NotificationTargetInput['data']): Record<string, unknown> {
  if (value && typeof value === 'object') return value;
  if (typeof value !== 'string') return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function canonicalizeNotificationLink(link: string | null | undefined): string | null {
  if (!link) return null;
  const legacyEmailPath = link.match(/^\/emails\/(\d+)(?:[?#].*)?$/);
  if (legacyEmailPath) return withReturnTo(`/emails?emailId=${legacyEmailPath[1]}`);
  const legacyTaskPath = link.match(/^\/tasks\/(\d+)(?:[?#].*)?$/);
  if (legacyTaskPath) return `/tasks?taskId=${legacyTaskPath[1]}`;
  try {
    const origin = typeof window === 'undefined' ? 'https://crm.local' : window.location.origin;
    const url = new URL(link, origin);
    if (url.pathname === '/emails') {
      const emailId = positiveId(url.searchParams.get('emailId') ?? url.searchParams.get('messageId'));
      return emailId ? withReturnTo(`/emails?emailId=${emailId}`) : '/emails';
    }
    if (url.pathname === '/tasks') {
      const taskId = positiveId(url.searchParams.get('taskId'));
      return taskId ? `/tasks?taskId=${taskId}` : '/tasks';
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return link;
  }
}

export function resolveNotificationTarget(notification: NotificationTargetInput): string | null {
  const data = notificationData(notification.data);
  if (typeof data.deepLink === 'string') return canonicalizeNotificationLink(data.deepLink);
  const roomId = positiveId(data.roomId);
  if (roomId) return `/?room=${roomId}&drawer=1`;
  if (notification.entityType === 'email' || data.kind === 'new_email') {
    const emailId = positiveId(notification.entityId ?? data.emailId ?? data.messageId);
    if (emailId) return withReturnTo(`/emails?emailId=${emailId}`);
  }
  if (notification.entityType === 'task' || (typeof data.kind === 'string' && data.kind.startsWith('task_'))) {
    const taskId = positiveId(notification.entityId ?? data.taskId);
    if (taskId) return `/tasks?taskId=${taskId}`;
  }
  return canonicalizeNotificationLink(notification.link);
}
